import React from "react";
import { NoxusInput } from "./InputField";
import { ChangeEvent, useMemo, useState } from "react";
import { SpotSelect } from "./SelectField";
import { Control, Controller } from "react-hook-form";
import { CSS } from "@dnd-kit/utilities";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  useSortable,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { GripVertical, Plus, Trash } from "lucide-react";
import { NoxusFormLabel } from "./NoxusFormLabel";
import { NoxusIconButton } from "../UI/NoxusIconButton";
import { Button } from "../shadcn/button";
import { NoxusTooltip } from "../UI/NoxusTooltip";
import { findMaxStringValue } from "@/utils/generic";

function SortableItem({
  id,
  children,
}: {
  id: string;
  children: React.ReactNode;
}) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style} className="w-full bg-white py-1">
      <div className="flex items-end">
        <div className="flex-1">{children}</div>
        <div
          {...attributes}
          {...listeners}
          className="p-3 -mb-0.5 cursor-grab text-primary"
        >
          <GripVertical size="1rem" fill="060C11" strokeWidth="1.25px" />
        </div>
      </div>
    </div>
  );
}

type DictTextAndSelectValue = Record<string, string> & { value: string };

interface IDictTextAndSelectArrayInputProps {
  label: string;
  tooltip?: string;
  value: DictTextAndSelectValue[];
  onChange: (value: DictTextAndSelectValue[]) => boolean;
  name?: string;
  name_labels: string[];
  name_prefixes: string[];
  value_label: string;
  values: string[];
  default_value: string;
}

const DictTextAndSelectArrayInput = ({
  label,
  tooltip,
  value,
  onChange,
  name,
  name_labels,
  name_prefixes,
  value_label,
  values,
  default_value,
}: IDictTextAndSelectArrayInputProps) => {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Create a unique key for each entry based on the first text field
  const getEntryKey = (entry: DictTextAndSelectValue) => {
    return entry[name_labels?.[0] || ""] || "";
  };

  const [inputs, setInputs] = useState<Record<string, string>[]>(
    value?.map((v) => {
      const inputObj: Record<string, string> = {};
      name_labels?.forEach((label) => {
        inputObj[label] = v[label] || "";
      });
      return inputObj;
    }) || []
  );

  const typesOptions = useMemo(() => {
    if (!values || values.length === 0) {
      return [];
    }
    return values.map((v) => ({ value: v, label: v }));
  }, [values]);

  const addEntry = () => {
    if (
      !name_prefixes ||
      !name_labels ||
      name_prefixes.length === 0 ||
      name_labels.length === 0
    ) {
      return;
    }

    // Find the max value for the first prefix
    const firstPrefix = name_prefixes[0];
    const firstLabel = name_labels[0];
    const maxOutputValue = findMaxStringValue(
      value.map((i) => i[firstLabel]),
      new RegExp(`^${firstPrefix}(\\d+)$`)
    );

    // Create new entry with all text fields
    const newEntry: DictTextAndSelectValue = { value: default_value };
    const newInputObj: Record<string, string> = {};

    name_labels.forEach((label, index) => {
      const prefix = name_prefixes[index] || "";
      const fieldName = `${prefix}${maxOutputValue + 1}`;
      newEntry[label] = fieldName;
      newInputObj[label] = fieldName;
    });

    if (onChange([...value, newEntry])) {
      setInputs([...inputs, newInputObj]);
    } else {
      setInputs([...inputs]);
    }
  };

  const handleDeleteClick = (index: number) => {
    if (value.length === 1) return;
    const newInputs = [...inputs];
    newInputs.splice(index, 1);
    setInputs(newInputs);
    if (onChange(value.filter((v, i) => i !== index))) {
      setInputs(newInputs);
    } else {
      setInputs([...inputs]);
    }
  };

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over) return;

    if (active.id !== over.id) {
      const activeIndex = value.findIndex((v) => getEntryKey(v) === active.id);
      const overIndex = value.findIndex((v) => getEntryKey(v) === over.id);

      const new_value = arrayMove(value, activeIndex, overIndex);
      const new_inputs = arrayMove(inputs, activeIndex, overIndex);

      onChange(new_value);
      setInputs(new_inputs);
    }
  }

  const onInputChange = (
    newValue: ChangeEvent<HTMLInputElement>,
    index: number,
    fieldLabel: string
  ) => {
    const newInputs = [...inputs];
    newInputs[index] = {
      ...newInputs[index],
      [fieldLabel]: newValue.currentTarget.value,
    };
    setInputs(newInputs);
  };

  const updateValueOnBlur = (
    event: React.FocusEvent<any, Element>,
    index: number,
    fieldLabel: string
  ) => {
    const oldInputs = structuredClone(inputs);
    const changeSuccess = onChange(
      value.map((v, i) => {
        if (i === index) {
          const _value = inputs[i][fieldLabel].trim() || v[fieldLabel];

          setInputs((inputs) => {
            return inputs.map((input, idx) => {
              if (idx === index) {
                return { ...input, [fieldLabel]: _value };
              }
              return input;
            });
          });
          return { ...v, [fieldLabel]: _value };
        }
        return v;
      })
    );

    if (!changeSuccess) {
      setInputs(oldInputs);
    }
  };

  return (
    <div>
      <NoxusFormLabel label={label} tooltip={tooltip} name={name} />
      <div className="w-full mt-4 flex flex-col gap-0">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
          modifiers={[restrictToVerticalAxis]}
        >
          <SortableContext
            items={value.map((v) => getEntryKey(v))}
            strategy={verticalListSortingStrategy}
          >
            {value.map((entry, index) => {
              const entryKey = getEntryKey(entry);
              return (
                <SortableItem key={entryKey} id={entryKey}>
                  <div key={index} className="w-full flex gap-4 items-end">
                    {name_labels?.map((fieldLabel, fieldIndex) => (
                      <div key={fieldLabel} className="flex-1">
                        <NoxusInput
                          value={inputs[index]?.[fieldLabel] || ""}
                          onChange={(e) => onInputChange(e, index, fieldLabel)}
                          onBlur={(e) =>
                            updateValueOnBlur(e, index, fieldLabel)
                          }
                          label={fieldLabel}
                        />
                      </div>
                    ))}
                    <div className="flex-1">
                      <SpotSelect
                        options={typesOptions}
                        value={typesOptions.find(
                          (o) => o.value === entry.value
                        )}
                        onChange={({ value: changeValue }) => {
                          onChange(
                            value.map((v, i) => {
                              if (i === index) {
                                return {
                                  ...v,
                                  value: changeValue,
                                };
                              }
                              return v;
                            })
                          );
                        }}
                        label={value_label}
                      />
                    </div>

                    <div className="mb-1">
                      <NoxusTooltip
                        content={
                          value.length === 1
                            ? "Can't have less than 1 value"
                            : ""
                        }
                        placement="top"
                        isDisabled={value.length !== 1}
                        asChild
                      >
                        <NoxusIconButton
                          aria-label="Delete"
                          icon={Trash}
                          disabled={value.length === 1}
                          variant="smallSecondary"
                          onClick={() => handleDeleteClick(index)}
                        />
                      </NoxusTooltip>
                    </div>
                  </div>
                </SortableItem>
              );
            })}
          </SortableContext>
        </DndContext>
      </div>
      <Button onClick={addEntry} className="mt-6 w-full" variant="outline">
        Add entry
        <Plus size="1.25rem" strokeWidth="1.25px" />
      </Button>
    </div>
  );
};

interface IDictTextAndSelectArrayFieldProps {
  name: string;
  label: string;
  tooltip?: string;
  defaultValue?: DictTextAndSelectValue[];
  control: Control<any>;
  onChange?: (value: DictTextAndSelectValue[]) => void;
  isDisabled?: boolean;
  name_labels: string[];
  name_prefixes: string[];
  value_label: string;
  values: string[];
  default_value: string;
}

export const DictTextAndSelectArrayField = ({
  name,
  label,
  tooltip,
  control,
  defaultValue,
  onChange,
  isDisabled,
  name_labels,
  name_prefixes,
  value_label,
  values,
  default_value,
}: IDictTextAndSelectArrayFieldProps) => {
  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue}
      disabled={isDisabled}
      render={({ field }) => {
        const handleOnChange = (value: DictTextAndSelectValue[]) => {
          // Check for repeated names in the first text field
          if (!name_labels || name_labels.length === 0) {
            field.onChange(value);
            if (onChange) onChange(value);
            return true;
          }

          const parsedLabels = new Set<string>();
          const firstLabel = name_labels[0];
          const hasRepeatedNames = value
            .map((v) => v[firstLabel])
            .some((label) => {
              if (parsedLabels.has(label)) {
                return true;
              }
              parsedLabels.add(label);
              return false;
            });

          if (!hasRepeatedNames) {
            field.onChange(value);
            if (onChange) onChange(value);
            return true;
          } else {
            // field.value has the old value, meaning it won't be updated
            if (onChange) onChange(field.value);
            return false;
          }
        };
        return (
          <DictTextAndSelectArrayInput
            name={name}
            label={label}
            tooltip={tooltip}
            value={field.value}
            onChange={handleOnChange}
            name_labels={name_labels}
            name_prefixes={name_prefixes}
            value_label={value_label}
            values={values}
            default_value={default_value}
          />
        );
      }}
    />
  );
};
